import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Group, Loader, Text } from '@mantine/core'
import { hideNotification, showNotification } from '@mantine/notifications'
import { IconListDetails } from '@tabler/icons-react'
import { formatHumanName } from '@medplum/core'
import { useMedplumProfile } from '@medplum/react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useClient } from '@/hooks/useClient'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useSessionState } from '@/hooks/useSessionState'
import { logError } from '@/utils/errorUtils'
import {
  useBriefingDay,
  type BriefingAction,
  type BriefingAppointment,
  type BriefingItem,
} from '../../hooks/useBriefingDay'
import { closeResult, uncloseResult } from '../../service'
import { CollapsibleSectionCard } from '../shared'
import { BriefingAttention } from './BriefingAttention'
import { BriefingDay } from './BriefingDay'
import { BriefingGreeting, type BriefingFilter } from './BriefingGreeting'
import { BriefingHiddenModal } from './BriefingHiddenModal'
import { doneKey, formatWhen, KIND_LABELS, quickAction, REASON_LABELS } from './briefingCopy'

const FILTER_EMPTY: Record<Exclude<BriefingFilter, 'all'>, string> = {
  'first-time': 'No first-time patients today.',
  returning: 'No returning patients today.',
  attention: 'No visits need your attention.',
}

// `at`: when the source write finished.
type Done = { did: string; undo: (() => Promise<unknown>) | null; at: number }

/**
 * The provider's daily briefing (PD-1936): today's visits on the left, everything that needs
 * the provider on the right. Reading it, opening an item or hiding one changes no source record.
 */
export function TodaysBriefing() {
  const profile = useMedplumProfile()
  const navigate = useNavigate()
  const [open, setOpen] = useSessionState('home:briefingOpen', true)
  const [filter, setFilter] = useState<BriefingFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [hiddenOpen, setHiddenOpen] = useState(false)
  // A minute clock for the timeline's "now" marker, past-visit fading and "still ahead".
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [])
  const briefingRef = useRef<HTMLDivElement>(null)
  const { query, setItemState, markStale } = useBriefingDay(true)
  const client = useClient()
  // "resend:<appointmentId>" / "copy:<appointmentId>" while that check-in link action runs.
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null)
  const middleware = useMiddlewareClient()
  // "Mark reviewed" / "Mark done" done on this screen, by item id, until a reload drops them.
  const [done, setDone] = useState<Map<string, Done>>(() => new Map())
  const [quickBusyId, setQuickBusyId] = useState<string | null>(null)
  const day = query.data
  const someUnavailable = day?.sources.some((s) => s.status === 'unavailable') ?? false

  // A server read that began after a mark and still lists the item means its source is open
  // again (restored in Results or another tab): the mark goes, so the item reads and counts as
  // open. Only server reads count: dismissing or reminding patches the cached day in place.
  const readStartedAt = useRef<number | null>(null)
  useEffect(() => {
    if (query.isFetching) {
      readStartedAt.current ??= Date.now()
      return
    }
    const startedAt = readStartedAt.current
    readStartedAt.current = null
    if (startedAt === null || query.isError || !day) return
    const listed = new Set(day.items.map(doneKey))
    setDone((prev) => {
      const reopened = [...prev].filter(([key, mark]) => mark.at < startedAt && listed.has(key))
      if (reopened.length === 0) return prev
      const next = new Map(prev)
      for (const [key] of reopened) next.delete(key)
      return next
    })
  }, [query.isFetching, query.isError, day])

  const view = useMemo(() => {
    const items = day?.items ?? []
    const appointments = day?.appointments ?? []
    const visible = items.filter((item) => item.state === null)
    const open = visible.filter((item) => !done.has(doneKey(item)))
    // Per visit: a Kate check-in belongs to one visit; everything else (messages, tasks,
    // results, guidance, notes) is about the patient, so every visit of theirs lists it.
    const byPatient = new Map<string, BriefingItem[]>()
    const kateByAppointment = new Map<string, BriefingItem[]>()
    for (const item of visible) {
      if (item.kind === 'kate-question' || item.kind === 'kate-callback') {
        if (!item.appointmentId) continue
        kateByAppointment.set(item.appointmentId, [
          ...(kateByAppointment.get(item.appointmentId) ?? []),
          item,
        ])
      } else {
        byPatient.set(item.patient.id, [...(byPatient.get(item.patient.id) ?? []), item])
      }
    }
    const itemsByAppointment = new Map<string, BriefingItem[]>()
    for (const a of appointments) {
      const list = [
        ...(kateByAppointment.get(a.appointmentId) ?? []),
        ...(a.patient ? (byPatient.get(a.patient.id) ?? []) : []),
      ]
      if (list.length > 0) itemsByAppointment.set(a.appointmentId, list)
    }
    const shownAppointments = appointments.filter((a) => {
      if (filter === 'first-time') return a.firstVisit === true
      if (filter === 'returning') return a.firstVisit === false
      if (filter === 'attention')
        return (itemsByAppointment.get(a.appointmentId) ?? []).some((i) => !done.has(doneKey(i)))
      return true
    })
    const selected = appointments.find((a) => a.appointmentId === selectedId)
    const patientScope = selected?.patient
      ? new Set([selected.patient.id])
      : filter === 'first-time' || filter === 'returning'
        ? new Set(shownAppointments.flatMap((a) => (a.patient ? [a.patient.id] : [])))
        : null
    return {
      appointments: shownAppointments,
      appointmentsById: new Map(appointments.map((a) => [a.appointmentId, a])),
      itemsByAppointment,
      items: patientScope ? visible.filter((i) => patientScope.has(i.patient.id)) : visible,
      hidden: items.filter((item) => item.state !== null),
      attention: open.length,
      ahead: appointments.filter((a) => a.start !== null && Date.parse(a.start) > now).length,
      focusName: selected?.patient?.name ?? null,
      filteredByChip: !selected?.patient && patientScope !== null,
    }
  }, [day, filter, selectedId, now, done])

  const act = (item: BriefingItem, change: BriefingAction) => {
    // A failed save rolls back and toasts in the hook.
    const request = setItemState(item, change)
    request.catch(() => {})
    if (change.action === 'restore') return
    const id = `briefing-${item.id}`
    showNotification({
      id,
      autoClose: 6000,
      message: (
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Text size="sm">
            {change.action === 'dismiss'
              ? `Dismissed · ${REASON_LABELS[change.reason]}`
              : `Reminder set · ${formatWhen(change.remindUntil)}`}
          </Text>
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={() => {
              hideNotification(id)
              void request
                .then(() => setItemState(item, { action: 'restore', undo: true }))
                .catch(() => {})
            }}
          >
            Undo
          </Button>
        </Group>
      ),
    })
    request.catch(() => hideNotification(id))
  }

  // The same staff actions as the schedule's Kate check-in card.
  const linkAction = async (appointment: BriefingAppointment, deliver: boolean) => {
    const { appointmentId } = appointment
    setLinkBusyId(`${deliver ? 'resend' : 'copy'}:${appointmentId}`)
    try {
      const result = await client.kateCheckInSend({ appointmentId, deliver })
      if (deliver) {
        const via = result.delivered.map((c) => (c === 'sms' ? 'text' : 'email')).join(' and ')
        showNotification({
          color: result.delivered.length ? 'green' : 'yellow',
          message: result.delivered.length
            ? `Check-in link sent again by ${via}.`
            : 'Link created, but the patient has no phone or email to send it to.',
        })
      } else {
        await navigator.clipboard.writeText(result.link)
        showNotification({ color: 'green', message: 'Check-in link copied.' })
      }
      void query.refetch()
    } catch (error) {
      logError('home', `Briefing check-in link ${deliver ? 'resend' : 'copy'}`, error)
      showNotification({
        color: 'red',
        // The server says why (e.g. the visit has already happened).
        message:
          (error as Error)?.message ||
          (deliver ? 'Could not send the check-in link.' : 'Could not copy the check-in link.'),
      })
    } finally {
      setLinkBusyId(null)
    }
  }

  // The product's own actions: close a result, mark the Kate answers reviewed, set a task Done.
  const runQuick = async (item: BriefingItem) => {
    const quick = quickAction(item)
    if (!quick) return
    setQuickBusyId(item.id)
    try {
      let undo: Done['undo'] = null
      let keys = [doneKey(item)]
      if (item.kind === 'result') {
        await closeResult(middleware, item.sourceId)
        undo = () => uncloseResult(middleware, item.sourceId)
      } else if (item.kind === 'task' && item.taskStatus) {
        const { columnId, value, groupId } = item.taskStatus
        await client.boardItemUpdate({ itemId: item.sourceId, values: { [columnId]: 'Done' } })
        undo = () =>
          client.boardItemUpdate({ itemId: item.sourceId, groupId, values: { [columnId]: value } })
      } else if (item.appointmentId) {
        // One review covers the whole check-in: its question and its callback.
        await client.kateCheckInReview({ appointmentId: item.appointmentId })
        keys = (day?.items ?? [])
          .filter((i) => i.appointmentId === item.appointmentId && i.kind.startsWith('kate-'))
          .map(doneKey)
      } else {
        return
      }
      const mark: Done = { did: quick.did, undo, at: Date.now() }
      setDone((prev) => {
        const next = new Map(prev)
        for (const key of keys) next.set(key, mark)
        return next
      })
      const id = `briefing-quick-${item.id}`
      const name = item.patient.name ?? 'Patient'
      showNotification({
        id,
        autoClose: 6000,
        message: (
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text size="sm">
              {quick.did}: {name}, {KIND_LABELS[item.kind].toLowerCase()}
            </Text>
            {undo && (
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() => {
                  hideNotification(id)
                  void undoQuick(item, undo)
                }}
              >
                Undo
              </Button>
            )}
          </Group>
        ),
      })
    } catch (error) {
      logError('home', `Briefing ${quick.label}`, error)
      showNotification({
        color: 'red',
        message: (error as Error)?.message || `Could not ${quick.label.toLowerCase()}.`,
      })
    } finally {
      setQuickBusyId(null)
    }
  }

  const undoQuick = async (item: BriefingItem, given?: Done['undo']) => {
    const undo = given ?? done.get(doneKey(item))?.undo
    if (!undo) return
    hideNotification(`briefing-quick-${item.id}`)
    try {
      await undo()
      setDone((prev) => {
        const next = new Map(prev)
        next.delete(doneKey(item))
        return next
      })
      // The source changed back: reload, as a reload may already have dropped the item.
      void query.refetch()
    } catch (error) {
      logError('home', 'Briefing undo', error)
      showNotification({ color: 'red', message: (error as Error)?.message || 'Could not undo.' })
    }
  }

  const openItem = (href: string) => {
    markStale()
    void navigate(href)
  }

  const family = profile?.resourceType === 'Practitioner' ? profile.name?.[0]?.family : undefined
  const doctorName =
    family ?? (profile?.name?.[0] ? formatHumanName(profile.name[0]) || null : null)

  return (
    <>
      <CollapsibleSectionCard
        icon={<IconListDetails size={20} className="text-[rgb(229,83,61)]" />}
        title="Today's briefing"
        subtitle={dayjs(now).format('ddd, MMM D')}
        // The greeting already counts what needs attention; the header carries the date only.
        count={0}
        countClassName=""
        open={open}
        onToggle={() => setOpen((v) => !v)}
      >
        {query.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader size="sm" />
          </div>
        ) : query.isError || !day ? (
          <Group gap="sm" justify="center" py="md">
            <Text size="sm" c="red">
              Couldn’t load today’s briefing.
            </Text>
            <Button variant="default" size="xs" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </Group>
        ) : (
          <div ref={briefingRef} className="flex flex-col">
            <BriefingGreeting
              doctorName={doctorName}
              now={now}
              visits={day.counts.visits}
              firstTime={day.counts.firstTime}
              returning={day.counts.returning}
              attention={view.attention}
              someUnavailable={someUnavailable}
              filter={filter}
              onFilterChange={(next) => {
                setFilter(next)
                setSelectedId(null)
              }}
            />
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
              <div className="flex min-w-0 flex-col gap-2.5 border-b border-gray-100 px-4 pt-3 pb-3.5 xl:border-r xl:border-b-0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2.5 gap-y-1">
                  <h4 className="text-sm font-bold text-gray-900">Your day</h4>
                  <span className="text-[13px] text-gray-600">
                    {filter === 'all'
                      ? `Click a visit for what Kate confirmed · ${view.ahead} of ${day.counts.visits} still ahead`
                      : `Showing ${view.appointments.length} of ${day.counts.visits} visits`}
                  </span>
                </div>
                <BriefingDay
                  appointments={view.appointments}
                  emptyText={
                    filter === 'all' || day.counts.visits === 0
                      ? 'No visits today.'
                      : FILTER_EMPTY[filter]
                  }
                  itemsByAppointment={view.itemsByAppointment}
                  now={now}
                  today={dayjs(now).format('YYYY-MM-DD')}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onResendLink={(appointment) => void linkAction(appointment, true)}
                  onCopyLink={(appointment) => void linkAction(appointment, false)}
                  linkBusyId={linkBusyId}
                  doneIds={new Set(done.keys())}
                />
              </div>
              <div className="flex min-w-0 px-4 pt-3 pb-3.5">
                <BriefingAttention
                  items={view.items}
                  hiddenCount={view.hidden.length}
                  appointmentsById={view.appointmentsById}
                  sources={day.sources}
                  generatedAt={day.generatedAt}
                  focusName={view.focusName}
                  filteredByChip={view.filteredByChip}
                  today={dayjs(now).format('YYYY-MM-DD')}
                  selectedAppointmentId={selectedId}
                  onSelectVisit={setSelectedId}
                  done={
                    new Map(
                      [...done].map(([id, d]) => [id, { did: d.did, canUndo: d.undo !== null }]),
                    )
                  }
                  quickBusyId={quickBusyId}
                  onQuick={(item) => void runQuick(item)}
                  onUndoQuick={(item) => void undoQuick(item)}
                  onShowHidden={() => setHiddenOpen(true)}
                  onOpen={openItem}
                  onAction={act}
                />
              </div>
            </div>
          </div>
        )}
      </CollapsibleSectionCard>
      <BriefingHiddenModal
        opened={hiddenOpen}
        onClose={() => setHiddenOpen(false)}
        items={view.hidden}
        onRestore={(item) => act(item, { action: 'restore' })}
        onClosedEmpty={() => {
          const root = briefingRef.current
          ;(
            root?.querySelector<HTMLElement>('[data-briefing-menu]') ??
            root?.querySelector<HTMLElement>('button')
          )?.focus()
        }}
      />
    </>
  )
}
